import React, { useMemo, useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Radio, Form, Input, Select, Button, Space, Typography, Tooltip, message } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import * as types from "reducer/actionTypes";
import { getOauthRequestUrl } from "script/oauthApi";

export default function ConnectType({
	init = {},
	selectedAdapterName,
	saving = false,
	connectParam,
}) {
	const [connect, setConnect] = useState(init.type || "aws-s3");
	const [form] = Form.useForm();
	const [extras, setExtras] = useState(() => {
		const initExtras = (init.extras || []).filter(Boolean);
		return initExtras.length ? initExtras : [{ key: "", value: "" }];
	});

	const dispatch = useDispatch();
	const initRef = useRef(false);

	const fetchSetConnectParameters = (value) => {
		dispatch({
			type: types.FETCH_SET_UNIADAPTER_CONNECT_PARAMETERS_REQUEST,
			value,
		});
	};
	const fetchGetConnectParameters = (value) => {
		dispatch({
			type: types.FETCH_GET_UNIADAPTER_CONNECT_PARAMETERS_REQUEST,
			value,
		});
	};

	const connectTypes = useMemo(
		() => ({
			"aws-s3": [
				{ name: "accessKey", label: "Access Key", required: true },
				{ name: "secretAccessKey", label: "Secret Access Key", required: true },
				{ name: "region", label: "Region", required: true },
				{ name: "bucket", label: "Bucket Name", required: true },
				{ name: "objectKey", label: "Object Key", required: true },
			],
			dropbox: [
				{ name: "clientId", label: "Client ID", required: true },
				{ name: "clientSecret", label: "Client Secret (password)", required: true },
				{ name: "redirectUri", label: "Redirect URI", required: true },
				{ name: "path", label: "Path (ex./docs/sample.docx)", required: true },
			],
			"google-drive": [
				{ name: "clientId", label: "Client ID", required: true },
				{ name: "clientSecret", label: "Client Secret (password)", required: true },
				{ name: "redirectUri", label: "Redirect URI", required: true },
				{ name: "fileId", label: "File Id", required: true },
			],
			minio: [
				{
					name: "endpoint",
					label: "Endpoint",
					required: true,
					placeholder: "https://minio.local:9000",
				},
				{ name: "accessKey", label: "Access Key", required: true },
				{ name: "secretAccessKey", label: "Secret Access Key", required: true },
				{ name: "bucket", label: "Bucket Name", required: true },
				{ name: "objectKey", label: "Object Key", required: true },
				{
					name: "useSSL",
					label: "Use SSL",
					required: false,
					inputType: "select",
					options: [
						{ label: "true", value: true },
						{ label: "false", value: false },
					],
				},
			],
			wopi: [
				{ name: "wopiUrl", label: "WOPI URL", required: true },
				{ name: "wopiPrivateKey", label: "Private Key", required: true },
				{ name: "wopiOldPrivateKey", label: "Old Private Key", required: false },
				{ name: "access_token", label: "Access Token", required: true },
				{ name: "access_token_ttl", label: "Access Token TTL (timestamp)", required: true },
				{ name: "docId", label: "Document ID", required: true },
			],
			// custom은 버튼으로 추가
			custom: [],
		}),
		[]
	);

	useEffect(() => {
		if (initRef.current) return;
		if (init?.params && selectedAdapterName) {
			const initType = init.type || connect;
			form.setFieldsValue({ [selectedAdapterName]: { [initType]: init.params } });
			initRef.current = true;
		}
	}, [init, selectedAdapterName, form, connect]);

	const mergeParams = () => {
		const allValues = form.getFieldsValue(true);
		const values = allValues?.[selectedAdapterName]?.[connect] || {};

		let params = { ...values };
		if (connect === "custom") {
			const extraKeyAndValue = extras
				.filter((e) => e && String(e.key).trim() !== "")
				.map((e) => ({ key: String(e.key).trim(), value: e.value }));
			const objExtra = Object.fromEntries(
				extraKeyAndValue.map((e) => [e.key, e.value])
			);
			params = { ...params, ...objExtra };
		}

		return {
			adapterName: selectedAdapterName,
			type: connect,
			params,
		};
	};

	useEffect(() => {
		mergeParams();
	}, [connect, extras]); // connect로 변경

	// 타입 전환 시 custom이 아니면 extras 초기화
	useEffect(() => {
		if (connect !== "custom") {
			setExtras([{ key: "", value: "" }]);
		}
	}, [connect]);

	const addExtra = () => setExtras((prev) => [...prev, { key: "", value: "" }]);
	const removeExtra = (idx) =>
		setExtras((prev) => prev.filter((_, i) => i !== idx));
	const updateExtra = (idx, patch) =>
		setExtras((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));

	const hasDuplicateKeys = () => {
		const keys = extras
			.filter((e) => e && String(e.key).trim() !== "")
			.map((e) => String(e.key).trim());
		return new Set(keys).size !== keys.length;
	};

	const handleSave = async () => {
		try {
			if (connect === "custom" && hasDuplicateKeys()) {
				message.error("The key is duplicated");
				return;
			}
			const mergeData = mergeParams();
			fetchSetConnectParameters(mergeData);
			fetchGetConnectParameters(selectedAdapterName);
			message.success("saved");
		} catch {
			message.error("error occurred");
		}
	};

	const renderField = (field) => {
		const { name, label, required, placeholder, inputType, options, inputProps } = field;
		const control =
			inputType === "select" ? (
				<Select options={options} allowClear />
			) : (
				<Input placeholder={placeholder} {...(inputProps || {})} />
			);
		return (
			<Form.Item
				key={`${selectedAdapterName}-${connect}-${name}`}
				name={[selectedAdapterName, connect, name]}
				label={label}
				rules={required ? [{ required: true, message: `${label} is required` }] : []}
			>
				{control}
			</Form.Item>
		);
	};

	// custom 이 아닐 땐 임의 값을 입력 못하게 함.
	const mapServerParam = (raw, ct) => {
		const t = raw.connectType || raw.type || "custom";
		const typeFields = (ct[t] || []).map((f) => f.name);

		const params = {};
		const _extras = [];

		Object.entries(raw || {}).forEach(([k, v]) => {
			if (k === "connectType" || k === "type") return;
			if (typeFields.includes(k)) params[k] = v;
			else _extras.push({ key: k, value: v });
		});

		return { type: t, params, extras: t === "custom" ? _extras : [] };
	};

	useEffect(() => {
		if (!connectParam) return;
		const { type: provider, params, extras: serverExtras } = mapServerParam(
			connectParam,
			connectTypes
		);
		setConnect(provider);
		form.resetFields([[selectedAdapterName]]);
		form.setFieldsValue({ [selectedAdapterName]: { [provider]: params } });
		setExtras(
			provider === "custom" && serverExtras.length
				? serverExtras
				: [{ key: "", value: "" }]
		);
	}, [connectParam, connectTypes, form, selectedAdapterName]);

	const oauthProvider = connect === "google-drive" || connect === "dropbox";

	const handleConnect = () => {
		if (!selectedAdapterName) {
			message.error("Choose Adapter");
			return;
		}
		if (connect === "custom" && hasDuplicateKeys()) {
			message.error("There are duplicated keys.");
			return;
		}
		const url = getOauthRequestUrl(connect, selectedAdapterName);

		const win = window.open(url, "oauth", "width=520, height=720");
		if (!win) {
			window.location.href = url;
			return;
		}

		const onMsg = (e) => {
			try {
				const data = e?.data || {};
				if (data.source === "oauth") {
					window.removeEventListener("message", onMsg);
					try {
						win.close();
					} catch { }
					if (data.status === "success") {
						message.success(`Connection succeed: ${data.accountEmail || ""}`);
						fetchGetConnectParameters(selectedAdapterName);
					} else {
						message.error("Connection failed");
					}
				}
			} catch { }
		};
		window.addEventListener("message", onMsg);
	};

	return (
		<Space direction="vertical" size="large" style={{ width: "100%" }}>
			<div>
				<Typography.Text strong style={{ marginRight: 12 }}>
					Connect Type
				</Typography.Text>
				<Radio.Group
					onChange={(e) => setConnect(e.target.value)}
					value={connect}
					optionType="button"
					buttonStyle="solid"
				>
					<Radio.Button value="aws-s3">AWS S3</Radio.Button>
					<Radio.Button value="dropbox">Dropbox</Radio.Button>
					<Radio.Button value="google-drive">Google Drive</Radio.Button>
					<Radio.Button value="minio">MinIO</Radio.Button>
					<Radio.Button value="wopi">WOPI</Radio.Button>
					<Radio.Button value="custom">custom</Radio.Button>
				</Radio.Group>
			</div>

			<Form form={form} layout="vertical" onValuesChange={mergeParams} initialValues={{}}>
				{(connectTypes[connect] || []).map(renderField)}
			</Form>

			{connect === "custom" && (
				<>
					<Space direction="vertical" style={{ width: "100%" }} size="small">
						{extras.map((row, idx) => {
							const duplicate =
								row.key &&
								extras.some(
									(e, i) => i !== idx && String(e.key).trim() === String(row.key).trim()
								);
							return (
								<Space key={idx} align="start" style={{ width: "100%" }}>
									<Tooltip
										title={duplicate ? "duplicated key" : ""}
										open={duplicate || undefined}
									>
										<Input
											style={{ width: 240 }}
											placeholder="key"
											status={duplicate ? "error" : undefined}
											value={row.key}
											onChange={(e) => updateExtra(idx, { key: e.target.value })}
											onBlur={mergeParams}
											onPressEnter={addExtra}
											allowClear
										/>
									</Tooltip>
									<Input
										placeholder="value"
										value={row.value}
										onChange={(e) => updateExtra(idx, { value: e.target.value })}
										onBlur={mergeParams}
										onPressEnter={addExtra}
										allowClear
									/>
									<Button
										danger
										icon={<DeleteOutlined />}
										onClick={() => removeExtra(idx)}
										disabled={extras.length === 1}
									/>
								</Space>
							);
						})}
						<Button
							icon={<PlusOutlined />}
							onClick={addExtra}
							type="dashed"
							style={{ width: "100%" }}
						>
							Add key
						</Button>
					</Space>
				</>
			)}

			<Space style={{ width: "100%", justifyContent: "flex-end" }}>
				<Button
					type="primary"
					onClick={handleSave}
					loading={saving}
					disabled={!selectedAdapterName}
				>
					Save
				</Button>
				{oauthProvider && (
					<Button onClick={handleConnect} disabled={!selectedAdapterName}>
						Connect
					</Button>
				)}
			</Space>
		</Space>
	);
}
